import React, { useEffect, useState, createContext, useContext } from 'react';
interface TableOrderState {
  tableId: string;
  tableNumber: string;
  tableName: string;
  restaurantSlug: string;
  restaurantId: string;
  orderChannel: 'qr' | 'waiter';
  createdAt?: number;
}
interface TableOrderContextType {
  tableOrder: TableOrderState | null;
  isTableOrder: boolean;
  setTableOrder: (state: TableOrderState) => void;
  clearTableOrder: () => void;
}
const TableOrderContext = createContext<TableOrderContextType | undefined>(undefined);
const TABLE_ORDER_KEY = 'noiza_table_order';
// Expire QR/table sessions after 1 hour. After that, a stale tableOrder
// in localStorage should NOT pollute the marketplace checkout experience
// for someone who just happened to scan a table QR earlier.
const TABLE_ORDER_TTL_MS = 60 * 60 * 1000;
function loadFromStorage(): TableOrderState | null {
  try {
    const raw = localStorage.getItem(TABLE_ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TableOrderState;
    // If there's no createdAt (legacy entries) or it's expired, clear it.
    const created = Number(parsed?.createdAt) || 0;
    if (!created || Date.now() - created > TABLE_ORDER_TTL_MS) {
      localStorage.removeItem(TABLE_ORDER_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(TABLE_ORDER_KEY);
    return null;
  }
}
export function TableOrderProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [tableOrder, setTableOrderState] = useState<TableOrderState | null>(() => loadFromStorage());
  useEffect(() => {
    if (tableOrder) {
      localStorage.setItem(TABLE_ORDER_KEY, JSON.stringify(tableOrder));
    } else {
      localStorage.removeItem(TABLE_ORDER_KEY);
    }
  }, [tableOrder]);
  const setTableOrder = (state: TableOrderState) => {
    // Always stamp createdAt on the way in so the TTL works.
    setTableOrderState({
      ...state,
      createdAt: Date.now()
    });
  };
  const clearTableOrder = () => {
    setTableOrderState(null);
    localStorage.removeItem(TABLE_ORDER_KEY);
  };
  return <TableOrderContext.Provider value={{
    tableOrder,
    isTableOrder: !!tableOrder,
    setTableOrder,
    clearTableOrder
  }} data-id="element-3212" data-mp="na2to3fy1ebw">
      {children}
    </TableOrderContext.Provider>;
}
export function useTableOrder() {
  const context = useContext(TableOrderContext);
  if (context === undefined) {
    throw new Error('useTableOrder must be used within a TableOrderProvider');
  }
  return context;
}